import { useCallback, useMemo, useRef } from 'react';
import type { FormikProps } from 'formik';
import { useGetAllUsers } from '@/hooks/useGetAllUsers';
import type { AutoRejectEmailSettings } from '@/api/aiScoring/aiScoring.api';
import type { AutoRejectEmailFormValues } from '../autoRejectEmail.types';
import {
  mapConfigToFormValues,
  mapFormValuesToEmailSettings,
  type AutoRejectEmailConfigResponse,
} from './autoRejectEmailPayload';

/** Errors shown when the rejection email is enabled but incomplete. */
export const TEMPLATE_REQUIRED_ERROR = 'Select a rejection email template';
export const SUBJECT_REQUIRED_ERROR = 'Email subject is required';
export const BODY_REQUIRED_ERROR = 'Email body is required';

/**
 * Shared glue for the auto-reject rejection-email island across the Scoring
 * agent surfaces (setup wizard, screener info view, Job Information edit view).
 * Owns the formRef the `AutoRejectEmailSection` writes to, derives restore
 * `initialValues` from the persisted config, and builds the save payload.
 */
export function useAutoRejectEmail(
  aiScoring?: AutoRejectEmailConfigResponse | null
) {
  const formRef = useRef<FormikProps<AutoRejectEmailFormValues> | null>(null);
  const { fromUsers } = useGetAllUsers();

  const initialValues = useMemo(
    () => mapConfigToFormValues(aiScoring, fromUsers),
    [aiScoring, fromUsers]
  );

  // Reads the live form values at save time; falls back to the restored values
  // if the section hasn't mounted (e.g. auto-reject disabled).
  const buildEmailSettings = useCallback((): AutoRejectEmailSettings => {
    const values = formRef.current?.values ?? initialValues;
    return mapFormValuesToEmailSettings(values);
  }, [initialValues]);

  // Returns a validation error (or null). Once the user opts into sending a
  // rejection email, a template must be selected and the subject + body
  // (prefilled from the template, editable) must be non-empty.
  const validateEmailConfig = useCallback((): string | null => {
    const values = formRef.current?.values ?? initialValues;
    if (!values.sendRejectEmail) return null;
    const template = values.selectedTemplate as { id?: number } | null;
    if (!template?.id) return TEMPLATE_REQUIRED_ERROR;
    if (!values.subject?.trim()) return SUBJECT_REQUIRED_ERROR;
    if (!values.body?.trim()) return BODY_REQUIRED_ERROR;
    return null;
  }, [initialValues]);

  return { formRef, initialValues, buildEmailSettings, validateEmailConfig };
}
